import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Inbox } from 'lucide-react';
import { PARKING_LOT_REASONS, type ParkingLotReason } from '@/constants/parkingLotReasons';

interface ParkingLotReasonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: ParkingLotReason) => void;
  onCancel: () => void;
}

export function ParkingLotReasonDialog({
  open,
  onOpenChange,
  onConfirm,
  onCancel,
}: ParkingLotReasonDialogProps) {
  const [selectedReason, setSelectedReason] = useState<ParkingLotReason>('vehicle_completed');

  const handleConfirm = () => {
    onConfirm(selectedReason);
    onOpenChange(false);
  };

  const handleCancel = () => {
    onCancel();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Inbox className="h-5 w-5 text-primary" />
            Why are you parking this ticket?
          </DialogTitle>
          <DialogDescription>
            Select a reason for moving this ticket to the Parking Lot.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <RadioGroup
            value={selectedReason}
            onValueChange={(value) => setSelectedReason(value as ParkingLotReason)}
            className="space-y-3"
          >
            {PARKING_LOT_REASONS.map((reason) => (
              <div
                key={reason.value}
                className="flex items-center space-x-3 rounded-lg border p-3 hover:bg-accent/50 transition-colors cursor-pointer"
                onClick={() => setSelectedReason(reason.value)}
              >
                <RadioGroupItem value={reason.value} id={`pl-${reason.value}`} />
                <Label
                  htmlFor={`pl-${reason.value}`}
                  className="flex-1 cursor-pointer"
                >
                  {reason.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleConfirm}>
            Confirm Move
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
